import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCopilot } from '../hooks/useCopilot'
import { SessionState } from '../state/types'
import { clientId } from '../util/id'
import { AutoGrowTextarea } from './AutoGrowTextarea'

// Two attachments can share both name and size (a re-scan of the same document, two pages
// exported at identical size) — `${name}-${size}` is not a safe React key. Each attachment gets
// its own durable id at pick time instead, reusing the feature's existing id generator.
interface Attachment { id: string; file: File }

interface ComposerProps { session: SessionState }

export function Composer({ session }: Readonly<ComposerProps>) {
  const { t } = useTranslation()
  const { answer } = useCopilot()
  const [text, setText] = useState('')
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const fileInput = useRef<HTMLInputElement>(null)
  const blocked = !!session.pendingConfirmation
  const canSend = !blocked && (text.trim().length > 0 || attachments.length > 0)

  const send = () => {
    if (!canSend) return
    answer(session.clientId, text.trim(), attachments.map((a) => a.file))
    setText('')
    setAttachments([])
  }

  return (
    <div className={`copilot-composer${blocked ? ' is-blocked' : ''}`}>
      <div className="copilot-composer__field">
        <AutoGrowTextarea
          className="form-control"
          placeholder={t('copilot.composer.placeholder')}
          value={text}
          minRows={2}
          maxRows={8}
          disabled={blocked}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
          data-testid="copilot-composer-input"
        />
        {attachments.length > 0 && (
          <ul className="copilot-composer__attached" data-testid="copilot-attached">
            {attachments.map((a) => (
              <li key={a.id}>
                <i className="fa fa-paperclip" aria-hidden="true" />
                {a.file.name}
                <button
                  type="button"
                  aria-label={t('copilot.composer.removeAttachmentNamed', { name: a.file.name })}
                  data-testid="copilot-remove-attachment"
                  onClick={() => setAttachments((prev) => prev.filter((p) => p.id !== a.id))}
                >
                  <i className="fa fa-times" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="copilot-composer__tools">
          <button
            type="button"
            className="copilot-composer__attach"
            data-testid="copilot-attach"
            disabled={blocked}
            onClick={() => fileInput.current?.click()}
          >
            <i className="fa fa-paperclip" aria-hidden="true" />
            {t('copilot.composer.attach')}
          </button>
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            disabled={blocked}
            data-testid="copilot-attach-input"
            onChange={(e) => {
              // Read the FileList into a plain array before resetting the input below: after the
              // reset, e.target.files is empty, so capturing it here (rather than reading
              // e.target.files lazily from inside the setState updater, which may run after this
              // handler returns) is what keeps the picked files from being lost.
              const picked = Array.from(e.target.files ?? []).map((file) => ({ id: clientId(), file }))
              setAttachments((prev) => [...prev, ...picked])
              e.target.value = ''   // let the same file be re-picked after a remove
            }}
          />
          <span className="copilot-composer__hint" data-testid="copilot-composer-hint">
            {t('copilot.composer.newline')}
          </span>
        </div>
      </div>
      <button
        type="button"
        className="btn btn-primary copilot-send"
        disabled={!canSend}
        onClick={send}
        data-testid="copilot-send"
      >
        {t('copilot.composer.send')}
      </button>
    </div>
  )
}
